import React, {ReactElement} from "react";
import {StyledCircularProgress, StyledSpan} from "@/shared/StyledComponents";
import {Typography} from "@mui/material";
import {
  Checkin,
  CheckinCheckin,
  CheckinCode,
  CheckinData,
  CheckinRegister,
  Checkins,
  CheckinType
} from "@/checkin-common/types";
import {PathComponent} from "@/shared";
import {
  BaseCheckinPlayerFragment,
  BaseCheckinPlayerFragmentProps,
  BaseCheckinPlayerFragmentState
} from "@/checkin-shared/BaseCheckinPlayerFragment";
import {WelcomeScreen} from "./WelcomeScreen";
import {ChooseVisitTypeScreen} from "./ChooseVisitTypeScreen";
import {FindAppointmentScreen} from "./FindAppointmentScreen";
import {ScanQrCodeScreen} from "./ScanQrCodeScreen";
import {EnterAppointmentCodeScreen} from "./EnterAppointmentCodeScreen";
import {NewPatientFormScreen} from "./NewPatientFormScreen";
import {EnterPhoneNumberScreen} from "./EnterPhoneNumberScreen";
import {AppointmentConfirmScreen} from "./AppointmentConfirmScreen";
import {PatientTypeScreen} from "./PatientTypeScreen";
import {ProfileConfirmScreen} from "./ProfileConfirmScreen";
import {VisitReasonScreen} from "./VisitReasonScreen";
import {ConsentFormScreen} from "./ConsentFormScreen";

export enum CheckinScreenType {
  WELCOME,
  CHOOSE_VISIT_TYPE,
  FIND_APPOINTMENT,
  PATIENT_TYPE,
  REGISTER_FORM,
  CHECKIN_QRCODE,
  ENTER_APPOINTMENT_CODE,
  ENTER_PHONE_NUMBER,
  APPOINTMENT_CONFIRM,
  CHECKIN_PROGRESS,
  CHECKIN_DONE,
  CHECKIN_ERROR,
  PROFILE_CONFIRM,
  VISIT_REASON,
  CONSENT_FORM,
}

export enum LookupType {
  APPOINTMENT = "APPOINTMENT",
  PROFILE = "PROFILE",
}

export type CheckinState = {
  code?: CheckinCode,
  type?: CheckinType,
  lookupType?: LookupType,
  checkin: CheckinCheckin,
  register: CheckinRegister,
}

export type CheckinPlayerFragmentProps = BaseCheckinPlayerFragmentProps & {}

type CheckinPlayerFragmentState = BaseCheckinPlayerFragmentState<CheckinScreenType> & {
  checkin: CheckinState,
}

export class CheckinPlayerFragment extends BaseCheckinPlayerFragment<CheckinScreenType, CheckinPlayerFragmentProps, CheckinPlayerFragmentState> {

  static nestedPaths(): PathComponent[] {
    return [];
  }

  protected onCreateState(): CheckinPlayerFragmentState {
    return {
      ...super.onCreateState(),
      screenStack: [CheckinScreenType.WELCOME],
      checkin: {
        checkin: new CheckinCheckin(),
        register: new CheckinRegister(),
      },
    };
  }

  private async doCheckin(): Promise<void> {
    let checkin: Checkin;
    switch (this.state.checkin.type) {
      case CheckinType.CHECKIN:
        checkin = Checkin.createNewCheckin(this.state.checkin.checkin);
        break;
      case CheckinType.REGISTER:
        checkin = Checkin.createNewRegister(this.state.checkin.register);
        break;
    }
    if (checkin) {
      await new Checkins(this.props.practicePage.id).addListItem(checkin);
    }
    this.setState({
      checkin: {
        ...this.state.checkin,
        type: null,
      },
    });
  }

  componentDidUpdate(prevProps: Readonly<CheckinPlayerFragmentProps>, prevState: Readonly<CheckinPlayerFragmentState>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    const prevScreenType = prevState.screenStack[prevState.screenStack.length - 1];
    if (prevScreenType !== CheckinScreenType.CHECKIN_PROGRESS && this.currentScreenType() === CheckinScreenType.CHECKIN_PROGRESS) {
      this.doCheckin()
        .then(() => {
          this.setScreenType(CheckinScreenType.CHECKIN_DONE);
        })
        .catch(() => {
          this.setScreenType(CheckinScreenType.CHECKIN_ERROR);
        });
    }
    if (this.currentScreenType() === CheckinScreenType.CHECKIN_DONE || this.currentScreenType() === CheckinScreenType.CHECKIN_ERROR) {
      setTimeout(() => {
        this.setScreenType(CheckinScreenType.WELCOME);
      }, 5_000);
    }
  }

  protected async fetchOnMount(): Promise<void> {
    await super.fetchOnMount();
    this.setState({
      checkin: {
        ...this.state.checkin,
        code: {
          practiceId: this.props.practicePage.id,
        },
      },
    });
  }

  protected renderScreen(): ReactElement {
    switch (this.currentScreenType()) {
      case CheckinScreenType.WELCOME:
        return this.renderWelcome();
      case CheckinScreenType.CHOOSE_VISIT_TYPE:
        return this.renderChooseVisitType();
      case CheckinScreenType.FIND_APPOINTMENT:
        return this.renderFindAppointment();
      case CheckinScreenType.PATIENT_TYPE:
        return this.renderPatientType();
      case CheckinScreenType.REGISTER_FORM:
        return this.renderRegisterForm();
      case CheckinScreenType.CHECKIN_QRCODE:
        return this.renderCheckinQrCode();
      case CheckinScreenType.ENTER_APPOINTMENT_CODE:
        return this.renderEnterAppointmentCode();
      case CheckinScreenType.ENTER_PHONE_NUMBER:
        return this.renderEnterPhoneNumber();
      case CheckinScreenType.APPOINTMENT_CONFIRM:
        return this.renderAppointmentConfirm();
      case CheckinScreenType.PROFILE_CONFIRM:
        return this.renderProfileConfirm();
      case CheckinScreenType.VISIT_REASON:
        return this.renderVisitReason();
      case CheckinScreenType.CONSENT_FORM:
        return this.renderConsentForm();
      case CheckinScreenType.CHECKIN_PROGRESS:
        return this.renderCheckinProgress();
      case CheckinScreenType.CHECKIN_DONE:
        return this.renderCheckinDone();
      case CheckinScreenType.CHECKIN_ERROR:
        return this.renderCheckinError();
    }
  }

  private renderWelcome() {
    return <WelcomeScreen
      practicePage={this.props.practicePage}
      checkinState={this.state.checkin}
      setCheckinState={checkin => this.setState({checkin: checkin})}
      pushScreenType={st => this.pushScreenType(st)}
    />
  }

  private renderChooseVisitType() {
    return <ChooseVisitTypeScreen
      practicePage={this.props.practicePage}
      checkinState={this.state.checkin}
      setCheckinState={checkin => this.setState({checkin: checkin})}
      pushScreenType={st => this.pushScreenType(st)}
    />
  }

  private renderPatientType() {
    return <PatientTypeScreen
      practicePage={this.props.practicePage}
      checkinState={this.state.checkin}
      setCheckinState={checkin => this.setState({checkin: checkin})}
      pushScreenType={st => this.pushScreenType(st)}
    />
  }

  private renderFindAppointment() {
    return <FindAppointmentScreen
      practicePage={this.props.practicePage}
      checkinState={this.state.checkin}
      setCheckinState={checkin => this.setState({checkin: checkin})}
      pushScreenType={st => this.pushScreenType(st)}
    />
  }

  private renderEnterAppointmentCode() {
    return <EnterAppointmentCodeScreen
      practicePage={this.props.practicePage}
      checkinState={this.state.checkin}
      setCheckinState={checkin => this.setState({checkin: checkin})}
      pushScreenType={st => this.pushScreenType(st)}
    />
  }

  private renderEnterPhoneNumber() {
    return <EnterPhoneNumberScreen
      practicePage={this.props.practicePage}
      checkinState={this.state.checkin}
      setCheckinState={checkin => this.setState({checkin: checkin})}
      pushScreenType={st => this.pushScreenType(st)}
      lookupType={this.state.checkin.lookupType || LookupType.APPOINTMENT}
    />
  }

  private renderAppointmentConfirm() {
    return <AppointmentConfirmScreen
      practicePage={this.props.practicePage}
      checkinState={this.state.checkin}
      setCheckinState={checkin => this.setState({checkin: checkin})}
      pushScreenType={st => this.pushScreenType(st)}
    />
  }

  private renderRegisterForm() {
    return <NewPatientFormScreen
      practicePage={this.props.practicePage}
      checkinState={this.state.checkin}
      setCheckinState={checkin => this.setState({checkin: checkin})}
      pushScreenType={st => this.pushScreenType(st)}
      setOnBackOverride={handler => this.onBackOverride = handler}
    />
  }

  private renderProfileConfirm() {
    return <ProfileConfirmScreen
      practicePage={this.props.practicePage}
      checkinState={this.state.checkin}
      setCheckinState={checkin => this.setState({checkin: checkin})}
      pushScreenType={st => this.pushScreenType(st)}
    />
  }

  private renderConsentForm() {
    return <ConsentFormScreen
      practicePage={this.props.practicePage}
      checkinState={this.state.checkin}
      setCheckinState={checkin => this.setState({checkin: checkin})}
      pushScreenType={st => this.pushScreenType(st)}
    />
  }

  private renderVisitReason() {
    return <VisitReasonScreen
      practicePage={this.props.practicePage}
      checkinState={this.state.checkin}
      setCheckinState={checkin => this.setState({checkin: checkin})}
      pushScreenType={st => this.pushScreenType(st)}
    />
  }

  private renderCheckinQrCode() {
    return <ScanQrCodeScreen
      practicePage={this.props.practicePage}
      checkinState={this.state.checkin}
      setCheckinState={checkin => this.setState({checkin: checkin})}
      pushScreenType={st => this.pushScreenType(st)}
    />
  }

  private renderCheckinProgress() {
    return <StyledCircularProgress/>;
  }

  private renderCheckinDone() {
    return <>
      <Typography variant="h6" style={{textAlign: "center"}}>
        All set! Please take a seat and someone will be with you shortly.
      </Typography>
      <StyledSpan/>
    </>;
  }

  private renderCheckinError() {
    return <>
      <StyledSpan/>
      <Typography variant="h6" style={{textAlign: "center"}}>
        Something went wrong. Please approach the reception for help.
      </Typography>
      <StyledSpan/>
    </>;
  }
}